import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const DEFAULTS = {
    brand_name: "Turbo Cimi",
    brand_short: "TC",
    top_banner: "[TURBO-CIMI] PARTS CATALOG // INDUSTRIAL PRECISION",
    top_banner_right: "Authorized access · Admin-managed",
    hero_kicker: "Turbo Cimi // Rev.",
    hero_line1: "Precision",
    hero_line2: "Turbocharger",
    hero_line3: "Parts.",
    hero_subtitle:
        "An admin-curated catalog for turbochargers, CHRA assemblies, nozzle rings, actuators and more.",
    hero_primary_cta: "Browse Catalog",
    hero_secondary_cta: "New arrivals",
    footer_tagline:
        "Precision turbocharger parts & industrial components. Admin-curated catalog.",
    footer_copyright: "Turbo Cimi · Precision parts platform",
    login_title: "Welcome back.",
    login_hero_line1: "Turbo Cimi",
    login_hero_line2: "precision",
    login_hero_line3: "parts catalog.",
    login_hero_subtitle:
        "A closed catalog for authorized dealers and workshops. Register to request access — the administrator will approve new accounts.",
    font_family: "Inter",
    font_size_base: 16,
    titles_bold: true,
    accents_italic: false,
    background_image_url: "",
    background_image_opacity: 0.06,
    logo_url: "",
    hero_background_url: "",
    hero_background_opacity: 0.18,
    hero_media_url: "",
    favicon_url: "",
    pd_bg_color: "",
    pd_panel_color: "",
    pd_text_color: "",
    pd_border_color: "",
    pd_accent_color: "",
    login_layout: "centered",
    login_form_style: "card",
    login_background_url: "",
    login_background_opacity: 0.4,
    login_background_blur: false,
    login_logo_size: 36,
    login_hero_line1_color: "#ffffff",
    login_hero_line2_color: "",
    login_hero_subtitle_color: "#94a3b8",
    login_form_text_color: "#0f172a",
    navbar_logo_size: 36,
    footer_logo_size: 36,
    button_style: "pill",
    hero_text_align: "left",
    hero_kicker_color: "",
    hero_title_color: "",
    hero_subtitle_color: "",
    contact_address: "",
    contact_city: "",
    contact_email: "",
    contact_phone: "",
    contact_hours: "",
    map_lat: null,
    map_lng: null,
    map_zoom: 15,
    invoice_logo_url: "",
    invoice_background_url: "",
    invoice_background_opacity: 0.08,
    invoice_footer_text: "",
    invoice_show_part_number: true,
    invoice_show_name: true,
    invoice_show_description: false,
    invoice_show_category: false,
    invoice_show_qty: true,
    invoice_show_unit_price: true,
    invoice_show_line_total: true,
    invoice_company_name: "",
    invoice_company_address: "",
    invoice_company_city: "",
    invoice_company_nipt: "",
    invoice_company_phone: "",
    invoice_company_email: "",
    invoice_company_iban: "",
    invoice_company_bank: "",
    invoice_reasons_title: "Arsyet e prishjes së turbokompresorit",
    invoice_service_note_title: "Shërbimet që duhen bërë te makina",
    invoice_title_invoice: "FATURË",
    invoice_subtitle_invoice: "",
    invoice_title_preventiv: "PREVENTIV",
    invoice_subtitle_preventiv: "",
    invoice_failure_reasons: [],
    invoice_services: [],
    invoice_paid_services: [],
    invoice_service_prices: {},
    currency_lek_per_eur: 100.0,
};

// Google Fonts we expose in the admin theme editor
export const FONT_OPTIONS = [
    { label: "Inter (Default)", value: "Inter", weights: "400;500;600;700;800;900" },
    { label: "Poppins", value: "Poppins", weights: "400;500;600;700;800;900" },
    { label: "Roboto", value: "Roboto", weights: "400;500;700;900" },
    { label: "Montserrat", value: "Montserrat", weights: "400;500;600;700;800;900" },
    { label: "Space Grotesk", value: "Space Grotesk", weights: "400;500;600;700" },
    { label: "JetBrains Mono", value: "JetBrains Mono", weights: "400;500;600;700" },
    { label: "Playfair Display", value: "Playfair Display", weights: "400;500;600;700;800;900" },
    { label: "Oswald", value: "Oswald", weights: "400;500;600;700" },
    { label: "Bebas Neue", value: "Bebas Neue", weights: "400" },
    { label: "Manrope", value: "Manrope", weights: "400;500;600;700;800" },
];

const GOOGLE_FONT_LINK_ID = "tc-google-font";

function loadGoogleFont(family, weights) {
    if (!family) return;
    const href = `https://fonts.googleapis.com/css2?family=${family.replace(/ /g, "+")}:wght@${weights}&display=swap`;
    let link = document.getElementById(GOOGLE_FONT_LINK_ID);
    if (!link) {
        link = document.createElement("link");
        link.rel = "stylesheet";
        link.id = GOOGLE_FONT_LINK_ID;
        document.head.appendChild(link);
    }
    if (link.href !== href) link.href = href;
}

const ContentContext = createContext(null);

export function SiteContentProvider({ children }) {
    const [content, setContent] = useState(DEFAULTS);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const { data } = await api.get("/site-content");
            setContent({ ...DEFAULTS, ...data });
        } catch {
            setContent(DEFAULTS);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);
    // Keep the browser-tab icon in sync with the admin favicon/logo
    useEffect(() => {
        const v = encodeURIComponent((content.favicon_url || content.logo_url || "default").slice(-24));
        document.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]').forEach((el) => {
            const size = el.rel === "apple-touch-icon" ? 180 : parseInt(el.getAttribute("sizes"), 10) || 48;
            el.href = el.rel === "apple-touch-icon" ? `/api/app-icon.png?size=180&v=${v}` : `/api/favicon.png?size=${size}&v=${v}`;
        });
    }, [content.favicon_url, content.logo_url]);

    // Apply typography + background to the document
    useEffect(() => {
        const font = FONT_OPTIONS.find((f) => f.value === content.font_family) || FONT_OPTIONS[0];
        loadGoogleFont(font.value, font.weights);
        const root = document.documentElement;
        root.style.setProperty("--font-body", `"${font.value}", system-ui, sans-serif`);
        root.style.setProperty("--font-display", `"${font.value}", system-ui, sans-serif`);
        root.style.setProperty("--font-size-base", `${content.font_size_base}px`);
        root.style.setProperty("--title-weight", content.titles_bold ? "900" : "600");
        root.style.setProperty("--accent-style", content.accents_italic ? "italic" : "normal");
        root.style.setProperty("--bg-image-opacity", String(content.background_image_opacity ?? 0.06));
        // Apply button-style class on body
        document.body.classList.remove("btn-pill", "btn-rounded", "btn-sharp");
        document.body.classList.add(`btn-${content.button_style || "sharp"}`);
        if (content.background_image_url) {
            root.style.setProperty("--bg-image", `url("${content.background_image_url}")`);
            document.body.classList.add("has-bg-image");
        } else {
            root.style.removeProperty("--bg-image");
            document.body.classList.remove("has-bg-image");
        }
    }, [content.font_family, content.font_size_base, content.titles_bold, content.accents_italic, content.background_image_url, content.background_image_opacity, content.button_style]);

    const updateContent = async (next) => {
        const { data } = await api.put("/site-content", next);
        setContent({ ...DEFAULTS, ...data });
    };

    return (
        <ContentContext.Provider value={{ content, loading, updateContent, defaults: DEFAULTS, refresh: load }}>
            {children}
        </ContentContext.Provider>
    );
}

export function useSiteContent() {
    const ctx = useContext(ContentContext);
    if (!ctx) throw new Error("useSiteContent must be used within SiteContentProvider");
    return ctx;
}
